import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchNearbyRoutes } from '@/api';
import { useConfigStore } from '@/store';
import type { Route } from '@/types';

const POLLING_INTERVAL = 20_000; // 20 seconds

/**
 * Hook for fetching and managing nearby routes with automatic polling
 */
export function useRoutes() {
  const { latLng, searchRadiusMeters, routeOrder, hiddenRoutes } = useConfigStore();

  return useQuery({
    queryKey: ['routes', latLng.latitude, latLng.longitude, searchRadiusMeters],
    queryFn: () =>
      fetchNearbyRoutes({
        latitude: latLng.latitude,
        longitude: latLng.longitude,
        maxDistance: searchRadiusMeters,
      }),
    refetchInterval: POLLING_INTERVAL,
    refetchIntervalInBackground: true, // Keep polling when tab is hidden (kiosk mode)
    staleTime: POLLING_INTERVAL - 1000, // Consider data stale just before next poll
    select: (routes) => processRoutes(routes, routeOrder, hiddenRoutes),
  });
}

/**
 * Hook to get raw routes data (unfiltered) for config panel
 */
export function useRawRoutes(): Route[] | undefined {
  const { latLng, searchRadiusMeters } = useConfigStore();
  const queryClient = useQueryClient();

  return queryClient.getQueryData<Route[]>([
    'routes',
    latLng.latitude,
    latLng.longitude,
    searchRadiusMeters,
  ]);
}

/**
 * Process routes: filter hidden, sort by custom order
 */
function processRoutes(
  routes: Route[],
  routeOrder: string[],
  hiddenRoutes: string[]
): Route[] {
  // Filter out hidden routes
  const visibleRoutes = routes.filter(
    (route) => !hiddenRoutes.includes(route.global_route_id)
  );

  // Sort by custom order
  return visibleRoutes.sort((a, b) => {
    const indexA = routeOrder.indexOf(a.global_route_id);
    const indexB = routeOrder.indexOf(b.global_route_id);

    // Routes not in custom order go to the end
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}
